/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWindowSize } from 'react-use';
import Confetti from 'react-confetti';
import { Capacitor } from '@capacitor/core';
import { Haptics, NotificationType } from '@capacitor/haptics';
import { TappedOrderData } from '../hooks/usePushNotifications.ts';
import { formatCurrency } from '../utils/formatters.ts';
import './OrderSoldModal.css';

interface OrderSoldModalProps {
  order: TappedOrderData | null;
  onClose: () => void;
}

const CONFETTI_SOUND = '/sounds/crowd-cheer.mp3';
const CONFETTI_DURATION_MS = 4000;

// Portaled directly onto <body>, same reasoning as Navigation's mobile
// menu - a fixed-position overlay triggered from deep inside the tree
// (a notification tap can land on any route) shouldn't be at the mercy of
// some ancestor's overflow/position/stacking-context.
const OrderSoldModal: React.FC<OrderSoldModalProps> = ({ order, onClose }) => {
  const { width, height } = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Fires once per tapped-open order: a one-shot confetti burst, a haptic
  // buzz, and a crowd-cheer sound effect to go with the confetti. Haptics
  // goes through the native plugin rather than navigator.vibrate() - iOS's
  // WKWebView has never implemented the Vibration API, so that call is a
  // silent no-op on iPhones; the Vibration API is kept only as a fallback
  // for a plain mobile browser (no native plugin available there).
  useEffect(() => {
    if (!order) {
      return;
    }

    setShowConfetti(true);
    const confettiTimer = setTimeout(() => setShowConfetti(false), CONFETTI_DURATION_MS);

    if (Capacitor.isNativePlatform()) {
      Haptics.notification({ type: NotificationType.Success }).catch((err) => {
        console.warn('Haptics.notification failed:', err);
      });
    } else if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(200);
    }

    const audio = new Audio(CONFETTI_SOUND);
    audioRef.current = audio;
    audio.play().catch((err) => {
      // Autoplay can be blocked if the tap didn't register as a user
      // gesture in time - not worth surfacing to the user over.
      console.warn('Order-sold sound effect failed to play:', err);
    });

    return () => {
      clearTimeout(confettiTimer);
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, [order]);

  if (!order) return null;

  return createPortal(
    <div className="order-sold-overlay" onClick={onClose}>
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={250}
          gravity={0.25}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 2200, pointerEvents: 'none' }}
        />
      )}
      <div className="order-sold-modal" onClick={(e) => e.stopPropagation()}>
        <div className="order-sold-header">
          <div>
            <h2>{order.isDraftOrder ? 'New Draft Order' : 'New Order!'}</h2>
            {order.orderNumber && <p className="order-sold-number">{order.orderNumber}</p>}
          </div>
          <button className="order-sold-close" onClick={onClose} aria-label="Close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="order-sold-items">
          {order.items.map((item, index) => (
            <div className="order-sold-item" key={index}>
              <div className="order-sold-item-info">
                <span className="order-sold-item-title">{item.title}</span>
                {item.variant && <span className="order-sold-item-variant">{item.variant}</span>}
              </div>
              <div className="order-sold-item-meta">
                <span className="order-sold-item-qty">×{item.quantity}</span>
                {item.price && (
                  <span className="order-sold-item-price">{formatCurrency(item.price)}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="order-sold-footer">
          <span className="order-sold-total-label">Total</span>
          <span className="order-sold-total-value">{formatCurrency(order.orderTotal)}</span>
        </div>

        <button className="order-sold-close-btn" onClick={onClose}>Close</button>
      </div>
    </div>,
    document.body
  );
};

export default OrderSoldModal;
